import { z } from "zod";

const RoleEnum = z.enum(["Admin", "عميل", "Client", "Buyer", "Seller"]);
const GenderEnum = z.enum(["Male", "Female"]);
const AccountTypeEnum = z.enum(["Individual", "Establishment"]);

export const createChangePasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      oldPassword: z.string().min(8, {
        message: t(
          "authentication.changePassword.validation.oldPasswordLength"
        ),
      }),
      newPassword: z.string().min(8, {
        message: t(
          "authentication.changePassword.validation.newPasswordLength"
        ),
      }),
      confirmPassword: z.string().min(8, {
        message: t(
          "authentication.changePassword.validation.confirmPasswordLength"
        ),
      }),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("authentication.changePassword.validation.passwordsMismatch"),
      path: ["confirmPassword"],
    });

export const ConfirmSchema = z.object({
  code: z.string(),
  email: z.string().email("authentication.validation.invalidEmail"),
});

export const ForgotPasswordSchema = z.object({
  email: z.string().email("authentication.validation.invalidEmail"),
});

export const createResetPasswordSchema = (t: (key: string) => string) =>
  z.object({
    email: z.string(),
    code: z.string(),
    newPassword: z
      .string()
      .min(8, {
        message: t("authentication.changePassword.validation.passwordLength"),
      })
      .refine(
        (value) => {
          // Regex to check for at least one uppercase, one lowercase, one number, and one special character
          return /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]).{8,}$/.test(
            value
          );
        },
        {
          message: t(
            "authentication.changePassword.validation.passwordComplexity"
          ),
        }
      ),
  });

export const createCompanySchema = (t: (key: string) => string) =>
  z
    .object({
      nameAr: z.string().min(1, t("company.validation.nameRequired")),
      nameEn: z.string().optional(),
      website: z.string(),
      address: z.string().optional(),
      region: z.string().min(1, t("company.validation.regionRequired")),
      city: z.string().min(1, t("company.validation.cityRequired")),
      registrationNumber: z
        .string()
        .min(1, t("company.validation.registrationNumberRequired")),
      ownerType: z.enum(["Owner", "Authorized"]),
      stampedAuthorizationFormUrl: z.string().optional().nullable(),
      authorizationFile: z
        .instanceof(File)
        .nullable()
        .optional()
        .refine(
          (file) => file !== null,
          t("company.validation.authorizationFileRequired")
        ),
      registrationExpirationDate: z.string().optional().nullable(),
      creationDate: z.string().optional(),
      type: z.string(),
      activities: z.string().optional(),
      categories: z.string(),
      logoMedia: z
        .union([z.instanceof(File), z.string()])
        .nullable()
        .optional(),
      plaseOfIssue: z.string(),
      turnover: z.preprocess(
        (val) => (val === "" ? undefined : Number(val)),
        z
          .number({
            required_error: t("company.validation.turnoverRequired"),
            invalid_type_error: t("company.validation.turnoverInvalid"),
          })
          .nonnegative(t("company.validation.turnoverNonNegative"))
      ),
      employeesNumber: z
        .preprocess((val) => (val === "" ? undefined : Number(val)), z.number())
        .optional(),
      registeringFile: z
        .instanceof(File)
        .nullable()
        .optional()
        .refine(
          (file) => file !== null,
          t("company.validation.registeringFileRequired")
        ),
      taxInformation: z
        .string({ message: t("company.validation.taxInformationRequired") })
        .min(1, t("company.validation.taxInformationRequired")),
    })
    .refine(
      (data) => {
        if (data.ownerType === "Authorized") {
          return (
            data.stampedAuthorizationFormUrl !== null &&
            data.stampedAuthorizationFormUrl !== ""
          );
        } else {
          data.stampedAuthorizationFormUrl = null;
          return true;
        }
      },
      {
        message: t("company.validation.authorizationFormRequired"),
        path: ["stampedAuthorizationForm"],
      }
    );
// .refine(
//   (data) => {
//     if (isEmpty(data.registrationExpirationDate)) {
//       return true;
//     }
//     const creationDate = new Date(data.creationDate);
//     const expireDate = new Date(data.registrationExpirationDate);

//     return expireDate > creationDate;
//   },
//   {
//     message: t("company.validation.expireDateInvalid"),
//     path: ["expireDate"],
//   }
// );

export const ProfileSchema = z
  .object({
    id: z.string().nullable(),
    nickName: z
      .string()
      .min(1, { message: "profile.validation.nickNameRequired" }),
    email: z
      .string()
      .email({ message: "company.validation.contactInfoInvalid" }),
    phoneNumber: z
      .string()
      .min(1, { message: "profile.validation.phoneNumberRequired" }),
    identityType: z.enum(["Citizen", "Resident"]),
    address: z.string(),
    role: RoleEnum,
    gender: GenderEnum.nullable(),
    accountType: AccountTypeEnum.nullable(),
    region: z.string().min(1, { message: "company.validation.regionRequired" }),
    city: z.string().min(1, { message: "company.validation.cityRequired" }),
    identityNo: z.string().nullable().optional(),
    residenceNo: z.string().nullable().optional(),
  })
  .refine(
    (data) => (data.identityType === "Resident" ? !!data.residenceNo : true),
    {
      message: "profile.validation.residenceNumberRequired",
      path: ["residenceNo"],
    }
  );
